
import { computed, defineComponent, onMounted, PropType, reactive } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import OrderCardStatusesLine from '@/modules/DispatcherModule/components/OrderCardStatusesLine/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// types
import { DispatcherTakeOrderCardT, ScheduleT } from '@/core/types/dispatcher.types'

// enums
import { DispatcherOrderStatusesEnum } from '@/core/enums/dispatcher.enums'

// realisations
import { dispatcher } from '@/core/realisations/dispatcher.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { DataState } from '@/core/classes/DataState.class'
import { PeriodT } from '@/core/types/companies.types'
import { orders } from '@/core/realisations/orders.realisation'

const WEEK_DAYS = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']

export default defineComponent({
    components: {
        TheButton,
        TheInput,
        OrderCardStatusesLine,
        Cheap,
        TheSelect,
    },
    props: {
        item: {
            type: Object as PropType<DispatcherTakeOrderCardT>,
            default: null,
        },
    },
    emits: ['start-submit-order', 'refresh', 'date-changed'],
    setup(props, { emit }) {
        const emitStartSubmitOrder = () => {
            emit('start-submit-order')
        }

        const emitRefresh = () => {
            emit('refresh')
        }

        const state = reactive({
            is_changing_date: false,
            is_loading: false,
            new_date: null as string | null,
            for_all_invoices: 0,
            periodId: null as number | null,
        })

        const data_state = reactive({
            periods: new DataState<PeriodT>({}),
        })

        const state_form$ = useVuelidate({ new_date: { required } }, state)

        const startChangeDate = () => {
            state.is_changing_date = true
            state.new_date = null
            state_form$.value.$reset()
        }

        const cancelChangeDate = () => {
            state.is_changing_date = false
        }

        const changeDate = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || !state.new_date) return

            try {
                state.is_loading = true

                const payload = {
                    order_id: props.item.order_id,
                    new_date: state.new_date,
                    for_all_invoices: state.for_all_invoices,
                    periodId: state.periodId,
                }
                await dispatcher.changeOrderTakeDate(payload)
                cancelChangeDate()
                emit('date-changed')
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const isCanChangeDate = computed(() => {
            if (
                !state.is_changing_date &&
                DispatcherOrderStatusesEnum[props.item.status] !==
                    DispatcherOrderStatusesEnum.taken &&
                DispatcherOrderStatusesEnum[props.item.status] !==
                    DispatcherOrderStatusesEnum.cargo_handling
            ) {
                return true
            }

            return false
        })

        const getAdditionalInfo = computed(() => {
            let result = ''

            if (props.item.additional_services) {
                if (props.item.additional_services.car)
                    result += result.length ? ', машина' : 'Машина'
                if (props.item.additional_services.crane)
                    result += result.length ? ', кран' : 'Кран'
                if (props.item.additional_services.grid)
                    result += result.length ? ', обрешетка' : 'Обрешетка'
                if (props.item.additional_services.hydraulic_trolley)
                    result += result.length ? ', рохля' : 'Рохля'
                if (props.item.additional_services.loader)
                    result += result.length ? ', грузчики' : 'Грузчики'
                if (props.item.additional_services.manipulator)
                    result += result.length ? ', манипулятор' : 'Манипулятор'
                if (props.item.additional_services.pallet)
                    result += result.length ? ', паллета' : 'Паллета'
                if (props.item.additional_services.soft_package)
                    result += result.length ? ', мягкая упаковка' : 'Мягкая упаковка'
            }

            return result
        })

        const getScheduleStr = (schedule: ScheduleT) => {
            if (schedule)
                return `${WEEK_DAYS[schedule.weekday]}: ${schedule.work_time_from} - ${
                    schedule.work_time_until
                }`

            return ''
        }

        const getScheduleStrFinal = (schedules: ScheduleT[]) => {
            let result: string[] = []
            let schedules_filtered: ScheduleT[] = []

            schedules.forEach((schedule) => {
                let schedule_found = schedules_filtered.find(
                    (s) =>
                        s.work_time_from === schedule.work_time_from &&
                        s.work_time_until === schedule.work_time_until
                )

                if (!schedules_filtered.length || !schedule_found) {
                    schedules_filtered.push(schedule)
                    result.push(getScheduleStr(schedule))
                } else {
                    let index_found = schedules_filtered.indexOf(schedule_found)

                    let str_arr = result[index_found].split(': ')
                    let new_str = `${str_arr[0].concat(`, ${WEEK_DAYS[schedule.weekday]}`)}`

                    result[index_found] = `${new_str}: ${str_arr[1]}`
                }
            })

            return result
        }

        const loadPeriods = async () => {
            data_state.periods.pagination.current_page = 1

            try {
                data_state.periods.is_loading = true
                const response = await orders.getPeriods()
                data_state.periods.list = response.data

                data_state.periods.pagination.total_pages = 1

                data_state.periods.is_failed = false
                data_state.periods.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.periods.failure.catchError(error)

                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_failed = true
                }
            } finally {
                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_loading = false
                }
            }
        }

        // computed

        const schedule_take = computed(() => {
            return props.item.company_schedule.filter((weekday) => weekday.schedule_type_id === 15)
        })

        const schedule_delivery = computed(() =>
            props.item.company_schedule.filter((weekday) => weekday.schedule_type_id === 16)
        )

        //

        onMounted(loadPeriods)

        //

        return {
            data_state,
            WEEK_DAYS,
            emitStartSubmitOrder,
            emitRefresh,
            state,
            state_form$,
            startChangeDate,
            cancelChangeDate,
            changeDate,
            isCanChangeDate,
            DispatcherOrderStatusesEnum,
            getAdditionalInfo,
            getScheduleStrFinal,
            // computed
            schedule_take,
            schedule_delivery,
            loadPeriods,
        }
    },
})
